/**
 * Navigation Data Structure
 * Easily configurable neural mesh navigation nodes
 */

export const defaultNavigationData = {
  meta: {
    title: 'Neural Mesh Navigator',
    version: '1.0.0',
    description: 'Synaptic navigation system'
  },
  nodes: [
    {
      id: 'home',
      label: 'Home',
      description: 'Main neural hub',
      icon: '🏠',
      url: '#home',
      position: { x: 0, y: 0, z: 0 },
      category: 'core',
      importance: 1.0,
      connections: ['about', 'projects', 'services', 'contact']
    },
    {
      id: 'about',
      label: 'About',
      description: 'Learn about us',
      icon: '👤',
      url: '#about',
      position: { x: -3, y: 1.5, z: 1 },
      category: 'core',
      importance: 0.8,
      connections: ['home', 'team', 'history']
    },
    {
      id: 'projects',
      label: 'Projects',
      description: 'Our work showcase',
      icon: '🚀',
      url: '#projects',
      position: { x: 3, y: 1, z: -1 },
      category: 'core',
      importance: 0.9,
      connections: ['home', 'case-studies', 'tech-stack']
    },
    {
      id: 'services',
      label: 'Services',
      description: 'What we offer',
      icon: '⚡',
      url: '#services',
      position: { x: 2, y: -1.5, z: 2 },
      category: 'core',
      importance: 0.85,
      connections: ['home', 'pricing', 'consultation']
    },
    {
      id: 'contact',
      label: 'Contact',
      description: 'Get in touch',
      icon: '📧',
      url: '#contact',
      position: { x: -2, y: -1, z: -2 },
      category: 'core',
      importance: 0.75,
      connections: ['home', 'support']
    },
    {
      id: 'team',
      label: 'Team',
      description: 'Meet our neurons',
      icon: '👥',
      url: '#team',
      position: { x: -4.5, y: 2.5, z: 0 },
      category: 'secondary',
      importance: 0.6,
      connections: ['about']
    },
    {
      id: 'history',
      label: 'History',
      description: 'Our journey',
      icon: '📜',
      url: '#history',
      position: { x: -3.5, y: 3, z: 2.5 },
      category: 'secondary',
      importance: 0.5,
      connections: ['about']
    },
    {
      id: 'case-studies',
      label: 'Case Studies',
      description: 'Deep dive analysis',
      icon: '📊',
      url: '#case-studies',
      position: { x: 4.5, y: 2, z: 0 },
      category: 'secondary',
      importance: 0.7,
      connections: ['projects']
    },
    {
      id: 'tech-stack',
      label: 'Tech Stack',
      description: 'Our neural tools',
      icon: '🔧',
      url: '#tech-stack',
      position: { x: 4, y: 0, z: -2.5 },
      category: 'secondary',
      importance: 0.65,
      connections: ['projects']
    },
    {
      id: 'pricing',
      label: 'Pricing',
      description: 'Investment plans',
      icon: '💎',
      url: '#pricing',
      position: { x: 3.5, y: -2.5, z: 1 },
      category: 'secondary',
      importance: 0.7,
      connections: ['services']
    },
    {
      id: 'consultation',
      label: 'Consultation',
      description: 'Book a session',
      icon: '🗓️',
      url: '#consultation',
      position: { x: 1, y: -3, z: 3 },
      category: 'secondary',
      importance: 0.55,
      connections: ['services', 'contact']
    },
    {
      id: 'support',
      label: 'Support',
      description: 'Help center',
      icon: '❓',
      url: '#support',
      position: { x: -3, y: -2.5, z: -1 },
      category: 'secondary',
      importance: 0.5,
      connections: ['contact']
    },
    {
      id: 'blog',
      label: 'Blog',
      description: 'Neural thoughts',
      icon: '📝',
      url: '#blog',
      position: { x: 0, y: 3, z: -2 },
      category: 'tertiary',
      importance: 0.45,
      connections: ['home', 'about']
    },
    {
      id: 'careers',
      label: 'Careers',
      description: 'Join the network',
      icon: '💼',
      url: '#careers',
      position: { x: -1, y: 2.5, z: 3 },
      category: 'tertiary',
      importance: 0.4,
      connections: ['team', 'about']
    }
  ],
  categories: {
    core: {
      color: '#00d4ff',
      glowIntensity: 1.0,
      size: 1.0
    },
    secondary: {
      color: '#00ff88',
      glowIntensity: 0.7,
      size: 0.75
    },
    tertiary: {
      color: '#ff6b9d',
      glowIntensity: 0.5,
      size: 0.6
    }
  }
}

/**
 * Create custom navigation data
 */
export function createNavigationData(nodes, options = {}) {
  return {
    meta: {
      title: options.title || 'Custom Navigation',
      version: options.version || '1.0.0',
      description: options.description || ''
    },
    nodes: nodes.map((node, index) => ({
      id: node.id || `node-${index}`,
      label: node.label || 'Node',
      description: node.description || '',
      icon: node.icon || '◉',
      url: node.url || `#${node.id}`,
      position: node.position || { 
        x: Math.cos(index * 0.8) * 3, 
        y: Math.sin(index * 0.5) * 2, 
        z: Math.sin(index * 0.3) * 2 
      },
      category: node.category || 'core',
      importance: node.importance || 0.5,
      connections: node.connections || []
    })),
    categories: options.categories || defaultNavigationData.categories
  }
}
